import { Dispatch } from "react"
import getStripe from "../../utils/stripejs"
import {
  State,
  Currency,
  Action,
  CheckoutActionKind,
  CartItem,
} from "../types/checkoutTypes"
import { sendPixelEvent } from "../../utils/pixel"
import { klaviyoTrackBehavior } from "../../utils/klaviyo"
import { CartProduct } from "../../ts/interfaces"
import { Product } from "../product"
import { isSubscriptionInCart } from "../shared"
import { isItemInCart } from "../shared/calcStateItems"

export const addItemToCart = (
  state: State,
  dispatch: Dispatch<Action>,
  quantity: number,
  productsToAdd: Product[],
  products: Product[],
  skipTracking?: boolean
) => {
  // Load product information
  getStripe()

  let cartItems = [...state.cartItems] //make a new array so we can keep a clean record of changes

  for (const product of productsToAdd) {
    const cartProduct = product.stripeProduct

    if (cartProduct) {
      // Send tracking events
      if (!skipTracking) {
        sendTracking(state, cartProduct, quantity)
      }

      cartItems.push({ productId: cartProduct.id, quantity })

      // Update global state
      dispatch({
        type: CheckoutActionKind.ADD_TO_CART,
        payload: { id: cartProduct.id, quantity },
      })
    }
    for (const product of productsToAdd) {
      addBundledProductsToCart(product, products, dispatch, cartItems)
    }
  }
}

export const addBundledProductsToCart = (
  product: Product,
  products: Product[],
  dispatch: Dispatch<Action>,
  cartItems: CartItem[],
  onlyForced?: boolean
) => {
  product.productBundles?.forEach(p => {
    const bundle = products.find(pr => pr.id === p.product.id)
    if (p.forced && bundle?.stripeProduct) {
      //force addition of a related product
      if (
        isItemInCart(bundle.stripeProduct, cartItems) ||
        (bundle.isRecurring() &&
          !bundle.isUsage() &&
          isSubscriptionInCart(cartItems))
      ) {
        //do not add a 2nd subscription
        return
      }

      cartItems.push({
        productId: bundle.stripeProduct.id,
        quantity: bundle.isUsage() ? 0 : 1,
      })

      console.log("add item forced", bundle.name)

      dispatch({
        type: CheckoutActionKind.ADD_TO_CART,
        payload: {
          id: bundle.stripeProduct.id,
          quantity: bundle.isUsage() ? 0 : 1,
        },
      })
    } else if (!onlyForced && bundle?.stripeProduct) {
      if (isItemInCart(bundle.stripeProduct, cartItems)) {
        //already in the cart
        return
      }
      // promt the user to add to cart
      dispatch({
        type: CheckoutActionKind.PROMPT_ADD_TO_CART,
        payload: {
          productId: bundle.stripeProduct.id,
          title: p.title,
          description: p.description,
          skipText: p.skipText,
        },
      })
    }
  })
}

const sendTracking = (
  state: State,
  cartProduct: CartProduct,
  quantity: number
) => {
  const { currency } = state

  const price =
    (cartProduct && cartProduct.prices && cartProduct.prices[currency]) || null

  if (cartProduct && quantity && state && price) {
    sendPixelEvent("AddToCart", {
      items: [{ ...cartProduct, quantity }],
      total: 0,
      currency,
    })

    if (window?._learnq) {
      klaviyoTrackBehavior(
        "Added to Cart",
        buildKlaviyoObject(cartProduct, quantity, price.unit_amount, currency)
      )
    }
  }
}

const buildKlaviyoObject = (
  cartProduct: CartProduct,
  quantity: number,
  price: number,
  currency: Currency
) => {
  return {
    content_type: "product",
    id: cartProduct.id,
    name: cartProduct.name,
    quantity: quantity,
    ...(price && {
      value: parseFloat((price / 100.0).toFixed(2)),
    }),
    currency: currency,
  }
}
